import type { BigFiveAnswer, BigFiveScores, ClaimInput } from '@llura/types';

import type { CoachState } from '../stores/coach.store';
import type { CovenantState } from '../stores/covenant.store';

interface RitualSlice {
  count: number | null;
  period: 'day' | 'week' | null;
  intensity: 'soft' | 'challenging' | 'push' | null;
  isConfigured: boolean;
}

interface IntroSlice {
  completed: boolean;
}

interface I18nSlice {
  locale: string | null;
}

interface PersonalitySlice {
  seen: boolean;
  scores: BigFiveScores | null;
  narrative: string | null;
  narrativeLocale: string | null;
  rawAnswers: BigFiveAnswer[] | null;
}

// Builds the /me/claim payload from the pre-auth localStorage stores. The
// server treats this as idempotent: each field is only written if there's no
// existing value, so re-claiming is safe.
export function snapshotForClaim(input: {
  coach: CoachState;
  covenant: CovenantState;
  ritual: RitualSlice;
  intro: IntroSlice;
  i18n: I18nSlice;
  personality: PersonalitySlice;
}): ClaimInput {
  const { coach, covenant, ritual, intro, i18n, personality } = input;

  const claim: ClaimInput = {};

  if (
    coach.isConfigured &&
    coach.gender &&
    coach.tone &&
    coach.name &&
    coach.avatarUrl &&
    coach.dailyCommand
  ) {
    claim.coach = {
      gender: coach.gender,
      tone: coach.tone,
      name: coach.name,
      avatarUrl: coach.avatarUrl,
      dailyCommand: coach.dailyCommand,
      isConfigured: true,
    };
  }

  if (covenant.hasAccepted) {
    claim.covenant = {
      boundaries: covenant.boundaries,
      safeWord: covenant.safeWord,
    };
  }

  if (ritual.isConfigured) {
    claim.ritual = {
      count: ritual.count,
      period: ritual.period,
      intensity: ritual.intensity,
      isConfigured: true,
    };
  }

  if (intro.completed) claim.introCompleted = true;
  if (i18n.locale) claim.locale = i18n.locale;

  // Personality: carry the seen gate either way (completed or skipped), plus
  // the scored profile when there is one. Skipping leaves scores null, so we
  // only attach `personality` when there's an actual result.
  if (personality.seen) {
    claim.personalitySeen = true;
    if (personality.scores && personality.narrative && personality.narrativeLocale) {
      claim.personality = {
        scores: personality.scores,
        narrative: personality.narrative,
        narrativeLocale: personality.narrativeLocale,
        ...(personality.rawAnswers ? { rawAnswers: personality.rawAnswers } : {}),
      };
    }
  }

  return claim;
}
